<template>
  <li
    class="navbar-item"
    :class="{ 'navbar-item_collapsed': isCollapsed, 'navbar-item_child-revealed': isListChildrenVisible }"
  >
    <component
      :is="children ? 'div' : 'router-link'"
      v-tooltip.bottom="{ content: getLabel(item), ...tooltipOptions, classes: 'rfx-tooltip rfx-tooltip_shift-right' }"
      :to="route"
      class="navbar-item__link "
      :class="{
        'navbar-item__link_active': isActiveClass,
        'navbar-item__link_collapsed': isCollapsed
      }"
      active-class="navbar-item__link_active"
      :exact="item.name === 'dashboard'"
      @click.exact="onItemClick(true)"
      @click.native="onItemClick(true)"
    >
      <span class="navbar-item__icon-wrap">
        <base-material-design-icon
          class="navbar-item__icon"
          :icon="item.icon"
        />
      </span>
      <span class="navbar-item__label">{{ getLabel(item) }}</span>
      <div
        v-if="isArrowVisible"
        class="navbar-item__arrow-container"
      >
        <div
          class="navbar-item__arrow"
          :style="{ 'left': isChildrenVisible ? '10px' : '9px' }"
          :class="{ 'navbar-item__arrow-revealed': isChildrenVisible }"
        />
      </div>
    </component>
    <ul
      v-if="isListChildrenVisible"
      class="navbar-item__child-list "
    >
      <li
        v-for="child in children.filter(child => child.visible)"
        :key="child.label"
        class="navbar-item__child"
      >
        <router-link
          :to="child.route"
          class="navbar-item__child-link"
          active-class="navbar-item__child-link_active"
          @click.native="clickOnRouter"
        >
          {{ child.label }}
        </router-link>
        <template v-if="child.children?.length">
          <div
            class="navbar-item__arrow-container navbar-item__arrow-container__child"
            @click.stop="isChildNextLevelVisible = !isChildNextLevelVisible"
          >
            <div
              v-if="isArrowVisible"
              class="navbar-item__arrow"
              :class="{ 'navbar-item__arrow-revealed': isChildNextLevelVisible }"
            />
          </div>
          <ul
            v-if="isChildNextLevelVisible"
            class="navbar-item__child-list__next-lvl"
          >
            <li
              v-for="item in child.children.filter(child => child.visible)"
              :key="item.label"
            >
              <router-link
                :to="getThirdLevelChildRoute(child.route, item.path)"
                class="navbar-item__child-link navbar-item__child-link__next-lvl"
                active-class="navbar-item__child-link_active"
                @click.native="clickOnRouter"
              >
                {{ item.navBarLabel }}
              </router-link>
            </li>
          </ul>
        </template>
      </li>
    </ul>
  </li>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'

/**
 * Component is used in {@link TheNavbar}
 */
export default {
  name: 'BaseMenuItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    isCollapsed: {
      type: Boolean,
      default: false
    },
    openedItem: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isChildrenVisible: false,
      isChildNextLevelVisible: false
    }
  },
  computed: {
    ...mapState(['isAgentModeEnabled', 'prevRoute']),
    route() {
      if (this.children) {
        return ''
      }
      return this.item.path ? this.item.path : '/'
    },
    children() {
      if (!this.item.children) {
        return
      }
      const children = _.cloneDeep(this.item.children.filter(item => item.visible))
      children.forEach(child => {
        const childRoute = child.route ? child.route : child.path
        child.route = `${this.item.path}/${childRoute}`
      })
      return children
    },
    isChildren() {
      return this.children && this.children.length
    },
    isArrowVisible() {
      return this.isChildren && !this.isCollapsed
    },
    isListChildrenVisible() {
      return this.isChildrenVisible && this.isArrowVisible
    },
    isActiveClass() {
      return this.$route.fullPath.startsWith(this.item.path)
    }
  },
  watch: {
    openedItem() {
      // to collapse children block if another route is active
      if ((this.openedItem !== this.item.name) && this.isChildrenVisible) {
        this.isChildrenVisible = false
      }
    }
  },
  methods: {
    onItemClick() {
      this.$emit('click', this.item.name)
      if (!this.isChildren) {
        return
      }
      this.isChildrenVisible = !this.isChildrenVisible
      this.isChildNextLevelVisible = this.isChildrenVisible
      // open corresponding page by click on child button
      // if (openChild && this.isChildrenVisible) {
      //   if (this.children[0].children && this.children[0].children.addChildToRoute && this.children[0].children.length) {
      //     this.$router.push(`${this.children[0].route}/${this.children[ 0 ].children[ 0 ].path}`)
      //   } else {
      //     this.$router.push(this.children[0].route)
      //   }
      // }
      this.$emit('reveal-menu')
    },
    clickOnRouter(e) {
      // this.$parent.$parent.isDataUpdating - in MainLayout it means that URL changed (handle url change)
      if (!this.isAgentModeEnabled || this.$parent.$parent.isDataUpdating) {
        return
      }
      this.$router.push({ name: 'skip_update' })
      this.$nextTick(() => {
        this.$router.push({ name: e.target.href.split('/').slice(-1).pop() })
      })
    },
    getLabel(item) {
      return item.navigatonLabel ? item.navigatonLabel : item.label
    },
    getThirdLevelChildRoute(secondLevelRoute, thirdLevelRoute) {
      if (this.$route.fullPath.includes(secondLevelRoute)) {
        return thirdLevelRoute
      }
      return `${secondLevelRoute}/${thirdLevelRoute}`
    }
  }
}
</script>

<style scoped lang="scss">
.navbar-item {
  display: flex;
  flex-direction: column;

  &_collapsed {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 15px;

    .navbar-item__icon-wrap {
      padding-left: 9px;
      width: 100%;
      margin-right: 100px;
    }
  }

  &__label {
    font-size: 15px;
  }

  &__label-container {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: $rfx-dark-march !important;
    font-weight: 500;
    height: 100%;
    flex: 1 1 100%;
    width: 100%;
  }

  &_child-revealed {
    flex-flow: wrap;
    height: 100%;
  }

  &__arrow,
  &__arrow-revealed {
    position: absolute;
    left: 9px;
    top: 10px;
    width: 5px;
    height: 5px;
    border-bottom: 2px solid $rfx-bright-blue;
    border-right: 2px solid $rfx-bright-blue;
    transform: rotate(-45deg);
  }

  &__arrow-revealed {
    transform: rotate(45deg);
  }

  &__arrow-container {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: auto;
    margin-right: 15px;
    width: 28px;
    height: 28px;
  }

  &__arrow-container__child {
    top: 6px;
    right: -15px;
    cursor: pointer;
    position: absolute;

    &:hover {
      background-color: rgba(40, 74, 255, .1);
    }
  }

  &__icon-wrap {
    display: flex;
    width: 24px;
    flex: 0 0 24px;
    padding-left: 18px;
    justify-content: center;
    margin-right: 12px;
    font-size: 22px;

  }

  &__link {
    display: flex;
    font-size: 15px;
    align-items: center;
    text-decoration: none;
    color: $rfx-dark-march !important;
    font-weight: 500;
    height: 40px;
    width: 100%;
    cursor: pointer;

    @mixin activeHoverState {
      border-radius: 4px;
      background-color: rgba(1, 22, 39, 0.1);
      color: $rfx-bright-turquoise;
    }

    &_collapsed {
      width: 40px;
    }

    &_active {
      @include activeHoverState
    }

    &:hover {
      @include activeHoverState
    }
  }

  &__child-list {
    list-style-type: none;
    margin-left: 28px;
    padding-left: 10px;
    border-left: 1px solid $rfx-light-steel;
    width: 100%;
  }

  &__child-list__next-lvl {
    list-style-type: none;
    margin: 4px 0 0 0;
    padding-left: 10px;
    border-left: 1px solid $rfx-light-steel;
    margin-left: 10px;

    li {
      padding: 3px 3px 3px 0;

      &:first-child {
        padding-top: 0;
      }

      &:last-child {
        padding-bottom: 0;
      }
    }
  }

  &__child {
    padding: 10px 0;
    position: relative;
    width: 100%;
  }

  &__child-link {
    text-decoration: none;
    color: $rfx-dark-march;
    font-size: 14px;
    font-weight: 500;

    &_active {
      color: $rfx-bright-blue;
    }
  }

  &__child-link__next-lvl {
    font-size: 13px;
    line-height: 24px;
  }
}
</style>
